import { useState } from "react";
import { Copy, KeyRound } from "lucide-react-native";
import { View, Text, Image, Linking, Platform } from "react-native";
import * as Clipboard from "expo-clipboard";
import {
  ActionBanner,
  CardSurface,
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  FieldMessage,
  IconButton,
  MetaText,
  MoonCheckbox,
  MoonFieldInput,
  PrimaryButton,
  SecondaryButton,
  TextActionButton,
  haptic,
  semanticColors,
} from "@louise/moon-ui";
import { useStytchUser } from "@louise/api/useStytch";
import {
  useEnrollPasskey,
  useSetup2FA,
  useVerify2FA,
} from "@src/packages/auth/hooks/use2FA";
import { useTranslations } from "@src/packages/i18n";
import { OTPInput } from "@src/packages/form/OTPInput/OTPInput";

const padlockIcon =
  require("../../../../assets/images/you-icons/padlock.png") as number;

const ISSUER = "Louise";

function chunkSecret(secret: string): string {
  return secret.replace(/(.{4})/g, "$1 ").trim();
}

function buildOtpauthUri(secret: string, label: string): string {
  const encodedLabel = encodeURIComponent(`${ISSUER}:${label}`);
  const params = new URLSearchParams({
    secret,
    issuer: ISSUER,
  });
  return `otpauth://totp/${encodedLabel}?${params.toString()}`;
}

type Props = {
  visible: boolean;
  onClose: () => void;
  onComplete: () => void;
};

type Step = "intro" | "scan" | "verify" | "recovery";

export default function Setup2FAModal({ visible, onClose, onComplete }: Props) {
  const t = useTranslations();
  const [step, setStep] = useState<Step>("intro");
  const [totpCode, setTotpCode] = useState("");
  const [setupData, setSetupData] = useState<{
    secret: string;
    qrCode: string;
    recoveryCodes: string[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recoveryAcknowledged, setRecoveryAcknowledged] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  const { user } = useStytchUser();
  const accountLabel = user?.emails?.[0]?.email ?? user?.user_id ?? "account";

  const setup2FA = useSetup2FA();
  const verify2FA = useVerify2FA();
  const enrollPasskey = useEnrollPasskey();

  const reset = () => {
    setStep("intro");
    setTotpCode("");
    setSetupData(null);
    setError(null);
    setRecoveryAcknowledged(false);
    setSecretCopied(false);
  };

  const handleClose = () => {
    // On the recovery step the codes are only shown once. Don't let an
    // accidental backdrop tap or back-button discard them.
    if (step === "recovery") return;
    reset();
    onClose();
  };

  const handleOpenInAuthenticator = async () => {
    if (!setupData) return;
    const uri = buildOtpauthUri(setupData.secret, accountLabel);
    const supported = await Linking.canOpenURL(uri).catch(() => false);
    if (supported) {
      await Linking.openURL(uri);
      haptic("buzz");
    } else {
      // No authenticator app installed — copy the secret so the user can
      // paste it once they install one. Show feedback so the tap doesn't
      // feel like a no-op.
      await Clipboard.setStringAsync(setupData.secret);
      setSecretCopied(true);
      haptic("buzz");
      setTimeout(() => setSecretCopied(false), 3000);
    }
  };

  const handleSetup = async () => {
    try {
      setError(null);
      const data = await setup2FA.mutateAsync();
      setSetupData(data);
      setStep("scan");
    } catch (err) {
      // Stytch's message is developer text ("[500] internal_server_error ...").
      console.warn("2FA setup failed:", err);
      setError(t("you.modals.setup_2fa.intro_error_default"));
    }
  };

  // Passkey enrollment is an alternative to the TOTP flow, not a step within
  // it: one WebAuthn registration gives the account a "hardware" second factor
  // (moon's SMS first factor is "possession"), which is enough to enroll in
  // MFA. On success we finish the modal the same way handleDone does — there
  // are no recovery codes to display for a passkey.
  const handleEnrollPasskey = async () => {
    try {
      setError(null);
      await enrollPasskey.mutateAsync();
      haptic("buzz");
      reset();
      onComplete();
    } catch (err) {
      console.warn("Passkey enrollment failed:", err);
      setError(t("you.modals.setup_2fa.passkey_error_default"));
    }
  };

  const handleVerify = async () => {
    try {
      setError(null);
      await verify2FA.mutateAsync({ totpCode });
      haptic("buzz");
      setStep("recovery");
    } catch {
      setError(t("you.modals.setup_2fa.verify_error_invalid"));
    }
  };

  const handleCopyRecoveryCodes = async () => {
    if (setupData?.recoveryCodes) {
      await Clipboard.setStringAsync(setupData.recoveryCodes.join("\n"));
      haptic("buzz");
    }
  };

  const handleDone = () => {
    haptic("buzz");
    reset();
    onComplete();
  };

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      closeButton={step === "recovery" ? false : "minimal"}
      dismissOnBackdropTap={step !== "recovery"}
      backdropTestID="setup-2fa-backdrop"
      layout="centered"
    >
      {step === "intro" && (
        <>
          <DialogIcon source={padlockIcon} width={80} height={88} />
          <View testID="setup-2fa-intro-title" style={{ marginTop: 16 }}>
            <DialogHeader
              align="center"
              title={t("you.modals.setup_2fa.intro_title")}
              subtitle={t("you.modals.setup_2fa.intro_message")}
              style={{ marginBottom: 20 }}
            />
          </View>
          {error ? (
            <ActionBanner
              variant="important"
              message={error}
              style={{ marginBottom: 16 }}
            />
          ) : null}
          <DialogActions>
            <SecondaryButton
              label={t("you.modals.setup_2fa.intro_cancel")}
              onPress={handleClose}
              flex={1}
            />
            <PrimaryButton
              label={t("you.modals.setup_2fa.intro_setup")}
              onPress={handleSetup}
              loading={setup2FA.isPending}
              flex={1}
            />
          </DialogActions>
          {/* The quieter alternative, under the main buttons. */}
          <TextActionButton
            label={t("you.modals.setup_2fa.passkey_button")}
            action="you.modals.setup_2fa.passkey_button"
            onPress={() => void handleEnrollPasskey()}
            disabled={enrollPasskey.isPending}
            icon={KeyRound}
            size="sm"
            centered
            testID="passkey-enroll-button"
            style={{ marginTop: 12 }}
          />
        </>
      )}

      {step === "scan" && setupData && (
        <>
          <DialogHeader
            title={t("you.modals.setup_2fa.scan_title")}
            subtitle={
              Platform.OS === "web"
                ? t("you.modals.setup_2fa.scan_message_web")
                : t("you.modals.setup_2fa.scan_message_mobile")
            }
            style={{ marginBottom: 20 }}
          />
          {Platform.OS !== "web" && (
            <>
              <SecondaryButton
                onPress={handleOpenInAuthenticator}
                label={t("you.modals.setup_2fa.scan_open_in_app")}
                style={{ marginBottom: secretCopied ? 4 : 12 }}
              />
              {secretCopied && (
                <MetaText style={{ textAlign: "center", marginBottom: 12 }}>
                  {t("you.modals.setup_2fa.scan_secret_copied")}
                </MetaText>
              )}
            </>
          )}
          <View style={{ alignItems: "center", marginBottom: 16 }}>
            <Image
              source={{ uri: setupData.qrCode }}
              style={{
                width: 200,
                height: 200,
                borderRadius: 8,
                backgroundColor: semanticColors.surface.default,
              }}
              accessibilityLabel={t("you.modals.setup_2fa.scan_qr_a11y")}
            />
          </View>
          <MetaText style={{ textAlign: "center", marginTop: 0 }}>
            {t("you.modals.setup_2fa.scan_manual_intro")}
          </MetaText>
          {/* The code sits centred in a read-only field, with copy at the
              end; a spacer the copy button's width keeps it truly centred. */}
          <MoonFieldInput
            paddingX={8}
            style={{ marginTop: 8, marginBottom: 20 }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                minHeight: 52,
                gap: 6,
              }}
            >
              <View style={{ width: 30 }} />
              <Text
                className="font-dm-sans"
                selectable
                style={{
                  flex: 1,
                  textAlign: "center",
                  fontSize: 14,
                  lineHeight: 20,
                  fontWeight: "600",
                  color: semanticColors.text.primary,
                  letterSpacing: 1,
                  fontVariant: ["tabular-nums"],
                }}
              >
                {chunkSecret(setupData.secret)}
              </Text>
              {/* The one control in the app that must not log what it
                  copies: that is the TOTP secret, an auth credential. The
                  button logs its own label instead. */}
              <IconButton
                accessibilityLabel={t("you.modals.setup_2fa.copy_setup_code")}
                action="copy_totp_secret"
                onPress={async () => {
                  await Clipboard.setStringAsync(setupData.secret);
                  haptic("buzz");
                }}
                hitSlop={8}
                size={30}
                surface="frosted"
              >
                <Copy
                  size={14}
                  color={semanticColors.text.primary}
                  strokeWidth={1.8}
                />
              </IconButton>
            </View>
          </MoonFieldInput>
          <PrimaryButton
            onPress={() => setStep("verify")}
            label={t("you.modals.setup_2fa.scan_next")}
            fullWidth
          />
        </>
      )}

      {step === "verify" && (
        <>
          <DialogHeader
            title={t("you.modals.setup_2fa.verify_title")}
            subtitle={t("you.modals.setup_2fa.verify_message")}
            style={{ marginBottom: 20 }}
          />
          <OTPInput
            length={6}
            appearance="light"
            hasError={!!error}
            onChange={(code) => {
              setTotpCode(code);
              setError(null);
            }}
          />
          {error ? (
            <FieldMessage centered style={{ marginTop: 10 }}>
              {error}
            </FieldMessage>
          ) : null}
          <PrimaryButton
            onPress={handleVerify}
            disabled={totpCode.length !== 6 || verify2FA.isPending}
            loading={verify2FA.isPending}
            label={t("you.modals.setup_2fa.verify_confirm")}
            fullWidth
            style={{ marginTop: 16 }}
          />
        </>
      )}

      {step === "recovery" && setupData && (
        <>
          {/*
           * The recovery step is only rendered after `verify2FA` resolves,
           * which requires BOTH the client-side `stytch.totp.authenticate`
           * AND the server-side `POST /me/2fa/enroll` to succeed, so
           * enrollment is fully complete. This testID gives that completed
           * state a stable DOM anchor so it is observable without depending
           * on the Stytch SDK's reactive user state propagating to the
           * account row badge (louise-patient-expo#1466).
           */}
          <View testID="setup-2fa-enrolled">
            <DialogHeader
              title={t("you.modals.setup_2fa.recovery_title")}
              subtitle={t("you.modals.setup_2fa.recovery_message")}
              style={{ marginBottom: 20 }}
            />
          </View>
          <CardSurface
            testID="setup-2fa-recovery-codes"
            style={{ padding: 16, marginBottom: 12 }}
          >
            {setupData.recoveryCodes.map((code) => (
              <Text
                key={code}
                className="font-dm-sans"
                selectable
                style={{
                  fontSize: 14,
                  fontWeight: "500",
                  color: semanticColors.text.primary,
                  textAlign: "center",
                  lineHeight: 24,
                  fontVariant: ["tabular-nums"],
                }}
              >
                {code}
              </Text>
            ))}
          </CardSurface>
          <SecondaryButton
            onPress={handleCopyRecoveryCodes}
            label={t("you.modals.setup_2fa.recovery_copy_all")}
            leading={
              <Copy
                size={16}
                color={semanticColors.action.tonal.foreground}
                strokeWidth={1.8}
              />
            }
            style={{ marginBottom: 12 }}
          />
          <MoonCheckbox
            value={recoveryAcknowledged}
            onChange={setRecoveryAcknowledged}
            label={t("you.modals.setup_2fa.recovery_acknowledge")}
            testID="setup-2fa-recovery-acknowledge"
            style={{ paddingVertical: 8, marginBottom: 12 }}
          />
          <PrimaryButton
            onPress={handleDone}
            disabled={!recoveryAcknowledged}
            label={t("you.modals.setup_2fa.recovery_done")}
            fullWidth
          />
        </>
      )}
    </Dialog>
  );
}
